import { Injector } from "@angular/core";
import { DX_AUTH_CONFIG } from "../config/dx-auth-config";
import { DxInMemoryStorageStrategy } from "./strategy/storage/dx-in-memory-storage-strategy";
import { DxAuthStorageStrategyType } from "../config/dx-auth-storage-strategy-type";
import { DxAppError } from "../../dx-core/domain/error/dx-app-error";
import { DxSessionFactory } from "../domain/user/dx-session-factory";
import { DxLocalStorageStrategy } from "./strategy/storage/dx-local-storage-strategy";
import { DxSessionStorageStrategy } from "./strategy/storage/dx-session-storage-strategy";
import { DxLogger } from "../../dx-core/services/dx-logger.service";
import { DxKeycloakSessionRefreshStrategy } from "./strategy/session-refresh/dx-keycloak-session-refresh-strategy.service";
import { DxAuthStrategyType } from "../config/dx-auth-stategy-type";
import { DxBasicSessionRefreshStrategy } from "./strategy/session-refresh/dx-basic-session-refresh-strategy.service";
import { map, mergeMap } from "rxjs/operators";
import { DxOperationResultType } from "../../dx-core/domain/common/dx-operation-result-type";
import { DxBasicValidationStrategy } from "./strategy/session-validation/dx-basic-validation-strategy";
import { DxRefreshTokenValidationStrategy } from "./strategy/session-validation/dx-refresh-token-validation-strategy";
import { DxServerDefinedSessionRefreshStrategy } from "./strategy/session-refresh/dx-server-defined-session-refresh-strategy.service";
import { DxServerDefinedValidationStrategy } from "./strategy/session-validation/dx-server-defined-validation-strategy";
import { DxServerStorageStrategy } from "./strategy/storage/dx-server-storage-strategy";
import * as i0 from "@angular/core";
import * as i1 from "../../dx-core/services/dx-logger.service";
/** Сервис, предоставляет функциональность для работы с ссесией пользователя на клиенте. */
export class DxSessionService {
    constructor(_injector, _logger) {
        this._injector = _injector;
        this._logger = _logger;
        const config = this._injector.get(DX_AUTH_CONFIG);
        if (config) {
            this._storage = DxSessionService._dispatchStorageStrategy(this._injector, config.authStorageStrategyType);
            this._sessionRefreshStrategy = DxSessionService._dispatchSessionRefreshStrategy(this._injector, config.authStrategyType);
            this._sessionValidationStrategy = DxSessionService._dispatchSessionValidationStrategy(this._injector, config.authStrategyType);
        }
        else {
            this._storage = this._injector.get(DxInMemoryStorageStrategy);
            this._sessionRefreshStrategy = this._injector.get(DxBasicSessionRefreshStrategy);
            this._sessionValidationStrategy = this._injector.get(DxBasicValidationStrategy);
        }
    }
    get storage() {
        return this._storage;
    }
    /** Создает сессию на основе объекта `DxUser`. */
    createSession(user) {
        const session = DxSessionFactory.createUserSession(user);
        this._storage.store(session);
        return session;
    }
    /** Сносит объект сессии из хранилища */
    closeSession() {
        this._storage.erase();
    }
    /** Проверяет валидность текущей сессии на клиенте. **/
    isSessionValid() {
        return this._storage.extract().pipe(map((session) => {
            return this._sessionValidationStrategy.validateSession(session);
        }));
    }
    /** Проверяет необходимость обновления токена. */
    needRefreshToken() {
        return this._storage.extract().pipe(map((session) => {
            if (session.tokenExpTime)
                this._logger.debug('Token exp. time: ' + new Date(session.tokenExpTime * 1000));
            const currentTime = Math.ceil(new Date().getTime() / 1000);
            return session.tokenExpTime && (currentTime > session.tokenExpTime);
        }));
    }
    /** Обновляет сессию на основании рефреш-токена и текушей стратегии обновления. */
    refreshSession() {
        return this._storage.extract().pipe(mergeMap((session) => {
            return this._sessionRefreshStrategy.refreshSession(session).pipe(map((result) => {
                if (DxOperationResultType.SUCCESS === result.type) {
                    this._storage.store(session);
                    this._logger.debug('Access token refreshed');
                }
                return result;
            }));
        }));
    }
    static _dispatchStorageStrategy(injector, type) {
        switch (type) {
            case DxAuthStorageStrategyType.IN_MEMORY:
                return injector.get(DxInMemoryStorageStrategy);
            case DxAuthStorageStrategyType.LOCAL_STORAGE:
                return injector.get(DxLocalStorageStrategy);
            case DxAuthStorageStrategyType.SESSION_STORAGE:
                return injector.get(DxSessionStorageStrategy);
            case DxAuthStorageStrategyType.SERVER_STORAGE:
                return injector.get(DxServerStorageStrategy);
            default:
                const error = new DxAppError();
                error.message = 'Передан неподдерживаемый тип стратегии хранения сессии';
                throw error;
        }
    }
    static _dispatchSessionRefreshStrategy(injector, type) {
        switch (type) {
            case DxAuthStrategyType.BASIC_AUTH:
                return injector.get(DxBasicSessionRefreshStrategy);
            case DxAuthStrategyType.JWT_TOKEN_AUTH:
                return injector.get(DxBasicSessionRefreshStrategy);
            case DxAuthStrategyType.DOMAIN_AUTH:
                return injector.get(DxBasicSessionRefreshStrategy);
            case DxAuthStrategyType.OAUTH2:
                return injector.get(DxBasicSessionRefreshStrategy);
            case DxAuthStrategyType.KEY_CLOAK_AUTH:
                return injector.get(DxKeycloakSessionRefreshStrategy);
            case DxAuthStrategyType.SERVER_DEFINED:
                return injector.get(DxServerDefinedSessionRefreshStrategy);
            default:
                const error = new DxAppError();
                error.message = 'Передан неподдерживаемый тип стратегии работы с сессиями!';
                throw error;
        }
    }
    static _dispatchSessionValidationStrategy(injector, type) {
        switch (type) {
            case DxAuthStrategyType.BASIC_AUTH:
                return injector.get(DxBasicValidationStrategy);
            case DxAuthStrategyType.JWT_TOKEN_AUTH:
                return injector.get(DxBasicValidationStrategy);
            case DxAuthStrategyType.DOMAIN_AUTH:
                return injector.get(DxBasicValidationStrategy);
            case DxAuthStrategyType.OAUTH2:
                return injector.get(DxBasicValidationStrategy);
            case DxAuthStrategyType.KEY_CLOAK_AUTH:
                return injector.get(DxRefreshTokenValidationStrategy);
            case DxAuthStrategyType.SERVER_DEFINED:
                return injector.get(DxServerDefinedValidationStrategy);
            default:
                const error = new DxAppError();
                error.message = 'Передан неподдерживаемый тип стратегии работы с сессиями!';
                throw error;
        }
    }
}
DxSessionService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function DxSessionService_Factory() { return new DxSessionService(i0.ɵɵinject(i0.INJECTOR), i0.ɵɵinject(i1.DxLogger)); }, token: DxSessionService, providedIn: "root" });
